import { useEffect, createContext } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Modal,
  Space,
  Spin,
  Card,
  Row,
  Col,
  Flex,
  Dropdown,
  MenuProps,
} from "antd";

import { DownOutlined } from "@ant-design/icons";

import ObjectForm from "components/Admin/ObjectForm";

import { RootState } from "Redux/store";
import { fetchOptionCriterion, updateOptionCriterion, createOptionCriterion, deleteOptionCriterion } from "Redux/Admin/DecisionManager/OptionCriteria/action";

import { settings, initialSettings, initialValues } from './const';
import { ADMIN_PATH } from "utils/routes";

import type { IOptionCriterion } from 'Redux/Admin/DecisionManager/OptionCriteria/type';

const OptionCriterion = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';

  const { entities, loading, error } = useSelector((state: RootState) => state.fetchOptionCriteria);

  const [modal, contextHolder] = Modal.useModal();

  const ReachableContext = createContext<string | null>(null);

  useEffect(() => {
    if (id && !isNew) {
      dispatch(fetchOptionCriterion(id));
    }
  }, [dispatch, isNew, id]);

  const onFinish = (values: any) => {
    if (isNew) {
      dispatch(createOptionCriterion(values, handleCreate));
    }
    else if (id) {
      dispatch(updateOptionCriterion(values, id));
    }
  };

  const handleCreate = (project: IOptionCriterion) => {
    if (project && project.id) {
      navigate(`${ADMIN_PATH}/decision-manager/option-criteria/${project.id}`);
    }
  }

  if (!id) {
    navigate(`${ADMIN_PATH}/decision-manager/option-criteria`, { replace: true });
    return null;
  }

  const optionCriterion = isNew ? initialValues : entities[id];

  if (!optionCriterion) {
    return <Spin />;
  }

  const menuItems: MenuProps['items'] = [
    {
      label: 'Delete Option-Criterion',
      key: '2',
      danger: true,
    }
  ];

  const modalConfig = {
    title: 'Delete the option-criterion?',
    content: <span>Are you sure you want to delete the option-criterion <b>{optionCriterion.id}</b>?</span>,
    confirmLoading: true
  };

  const handleMenuClick: MenuProps['onClick'] = async (e) => {
    if (e.key === '2') {
      const confirmed = await modal.confirm(modalConfig);

      if (confirmed) {
        dispatch(deleteOptionCriterion(id, () => {
          navigate(`${ADMIN_PATH}/decision-manager/option-criteria`, { replace: true });
        }));
      }
    }
  };

  const menuProps = {
    items: menuItems,
    onClick: handleMenuClick,
  };

  return (
    <ReachableContext.Provider value="Light">
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Row>
          <Col span={12}>
            <Flex
              gap="middle"
              align="center"
              justify="space-between"
              style={{ marginBottom: 16 }}
            >
              <Link to={`${ADMIN_PATH}/decision-manager/option-criteria`}>Back</Link>
            </Flex>

            <Card title={`${isNew ? 'Create' : '' } OptionCriterion`} extra={
              isNew ? null : <Dropdown menu={menuProps} placement="bottom">
                <Button>
                  <Space>
                    Manage Option-Criterion
                    <DownOutlined />
                  </Space>
                </Button>
              </Dropdown>
            }>
              <ObjectForm
                data={optionCriterion}
                errorData={error}
                isNew={isNew}
                settings={isNew ? initialSettings : settings}
                loading={loading}
                onFinish={onFinish}
              />
            </Card>
          </Col>
        </Row>
      </div>

      {contextHolder}
    </ReachableContext.Provider>
  );
}

export default OptionCriterion;
